import { useTranslation } from 'react-i18next';
import {
  PERMISSIONS_BY_AREA,
  PERMISSION_AREAS,
  PERMISSION_LABELS,
  type PermissionKey,
} from '@gc/shared';

/**
 * The privileges checkbox matrix (SPEC §6.4, §3), extracted from the employee
 * drawer so the ROLES screen renders the exact same catalogue — a permission
 * added to the platform appears in both places with no change to either.
 *
 * Generated from `PERMISSIONS_BY_AREA` / `PERMISSION_LABELS` in `@gc/shared`,
 * the same lists the API validates against. Purely presentational: the owner
 * of `granted` decides what a tick means (employee grant vs. template
 * content).
 */
export function PermissionMatrix({
  granted,
  disabled,
  onChange,
}: {
  granted: ReadonlySet<PermissionKey>;
  /** Read-only rather than hidden — owners still SEE the full set they hold. */
  disabled?: boolean;
  onChange: (next: Set<PermissionKey>) => void;
}) {
  const { t } = useTranslation();

  function toggle(key: PermissionKey, checked: boolean): void {
    const next = new Set(granted);
    if (checked) next.add(key);
    else next.delete(key);
    onChange(next);
  }

  function toggleArea(area: (typeof PERMISSION_AREAS)[number], checked: boolean): void {
    const next = new Set(granted);
    for (const key of PERMISSIONS_BY_AREA[area]) {
      if (checked) next.add(key);
      else next.delete(key);
    }
    onChange(next);
  }

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {PERMISSION_AREAS.map((area) => {
        const keys = PERMISSIONS_BY_AREA[area];
        const allOn = keys.every((key) => granted.has(key));

        return (
          <fieldset key={area} className="rounded-control border border-subtle bg-input p-3">
            <legend className="flex items-center gap-2 px-1">
              <input
                type="checkbox"
                checked={allOn}
                disabled={disabled}
                onChange={(event) => toggleArea(area, event.target.checked)}
                aria-label={t('settings.employees.toggleArea', {
                  area: t(`settings.employees.area.${area}`),
                })}
                className="h-4 w-4 accent-[var(--brand-primary)] disabled:opacity-50"
              />
              <span className="text-xs font-semibold uppercase tracking-wide text-content-secondary">
                {t(`settings.employees.area.${area}`)}
              </span>
            </legend>

            <div className="mt-1 space-y-1.5">
              {keys.map((key) => (
                <label key={key} className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    checked={granted.has(key)}
                    disabled={disabled}
                    onChange={(event) => toggle(key, event.target.checked)}
                    className="mt-0.5 h-4 w-4 accent-[var(--brand-primary)] disabled:opacity-50"
                  />
                  <span className="text-sm text-content">
                    {/* Falls back to the English label from @gc/shared until a
                        locale supplies permissions.<key>. */}
                    {t(`permissions.${key}`, { defaultValue: PERMISSION_LABELS[key] })}
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
        );
      })}
    </div>
  );
}
